/**
 * LanguageContext
 *
 * Manages the current language selection for the application.
 * Language selection is based on merchant's supported languages.
 * Priority: localStorage > browser language > merchant default language
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { ADMIN_LANGUAGE_CODES } from '../lib/i18n';

export interface LanguageConfig {
  code: string;
  label: string;
  flag: string;
}

// Re-exported for callers already importing it from here. The list itself
// lives with the i18next config, which derives supportedLngs from it so the
// two cannot drift apart — see src/lib/i18n.ts for the three language sets.
export { ADMIN_LANGUAGE_CODES };

/**
 * Every language a merchant may offer its customers. Far wider than the set
 * the admin panel itself is translated into; most entries have no locale file,
 * which only means their labels cannot be auto-generated, not that they are
 * unsupported.
 */
export const LANGUAGE_CONFIGS: Record<string, LanguageConfig> = {
  en: { code: 'en', label: 'English', flag: '🇬🇧' },
  de: { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  fr: { code: 'fr', label: 'Français', flag: '🇫🇷' },
  es: { code: 'es', label: 'Español', flag: '🇪🇸' },
  it: { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  nl: { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  pt: { code: 'pt', label: 'Português', flag: '🇵🇹' },
  da: { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  sv: { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  no: { code: 'no', label: 'Norsk', flag: '🇳🇴' },
  fi: { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
  pl: { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  cs: { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
  el: { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  hr: { code: 'hr', label: 'Hrvatski', flag: '🇭🇷' },
  hu: { code: 'hu', label: 'Magyar', flag: '🇭🇺' },
  ro: { code: 'ro', label: 'Română', flag: '🇷🇴' },
  sk: { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
  sl: { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
  bg: { code: 'bg', label: 'Български', flag: '🇧🇬' },
  ja: { code: 'ja', label: '日本語', flag: '🇯🇵' },
  zh: { code: 'zh', label: '中文', flag: '🇨🇳' },
  ko: { code: 'ko', label: '한국어', flag: '🇰🇷' },
  ar: { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  tr: { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  ru: { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  uk: { code: 'uk', label: 'Українська', flag: '🇺🇦' },
};

interface LanguageContextType {
  currentLanguage: string;
  // All languages the merchant supports — used for authoring content/translations.
  availableLanguages: LanguageConfig[];
  // Subset of availableLanguages that have admin-panel UI translations —
  // used for the admin UI language switcher only.
  availableAdminLanguages: LanguageConfig[];
  defaultLanguage: string;
  changeLanguage: (language: string) => void;
  getLanguageConfig: (code: string) => LanguageConfig;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedLanguage';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { merchantId } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');
  const [merchantDefaultLanguage, setMerchantDefaultLanguage] = useState<string>('en');
  const [merchantSupportedLanguages, setMerchantSupportedLanguages] = useState<string[]>(['en']);
  const [initialized, setInitialized] = useState(false);

  // Fetch merchant settings when profile loads
  useEffect(() => {
    if (!merchantId) {
      setInitialized(true);
      return;
    }

    const fetchMerchantSettings = async () => {
      try {
        const { supabase } = await import('../lib/supabase');
        const { data, error } = await supabase
          .from('merchants')
          .select('default_language, supported_languages')
          .eq('id', merchantId!)
          .single();

        if (error) {
          console.error('[LanguageContext] Error fetching merchant settings:', error);
          setInitialized(true);
          return;
        }

        const defaultLang = (data?.default_language as string) || 'en';
        const supportedLangs = Array.isArray(data?.supported_languages)
          ? (data.supported_languages as string[])
          : ['en'];

        setMerchantDefaultLanguage(defaultLang);
        setMerchantSupportedLanguages(supportedLangs);

        // Language priority: 1. localStorage  2. Browser language (if supported)  3. Merchant default
        const storedLang = localStorage.getItem(STORAGE_KEY);
        const browserLang = navigator.language.split('-')[0];

        let initialLanguage = defaultLang;
        if (storedLang && supportedLangs.includes(storedLang) && ADMIN_LANGUAGE_CODES.includes(storedLang)) {
          initialLanguage = storedLang;
        } else if (supportedLangs.includes(browserLang) && ADMIN_LANGUAGE_CODES.includes(browserLang)) {
          initialLanguage = browserLang;
        }

        localStorage.setItem(STORAGE_KEY, initialLanguage);

        setCurrentLanguage(initialLanguage);
        setInitialized(true);
      } catch (error) {
        console.error('[LanguageContext] Error in fetchMerchantSettings:', error);
        setInitialized(true);
      }
    };

    fetchMerchantSettings();
  }, [merchantId]);

  const changeLanguage = useCallback(
    (language: string) => {
      if (!merchantSupportedLanguages.includes(language)) {
        console.warn(
          `[LanguageContext] Language "${language}" is not supported by merchant. Supported: ${merchantSupportedLanguages.join(', ')}`
        );
        return;
      }

      // The switcher only ever offers availableAdminLanguages, so this holds
      // already; enforcing it here keeps a future caller from putting the
      // panel into a language i18next cannot resolve.
      if (!ADMIN_LANGUAGE_CODES.includes(language)) {
        console.warn(
          `[LanguageContext] Language "${language}" has no admin UI translation. Supported: ${ADMIN_LANGUAGE_CODES.join(', ')}`
        );
        return;
      }

      setCurrentLanguage(language);
      localStorage.setItem(STORAGE_KEY, language);

      // Notify i18next (will be integrated later)
      const event = new CustomEvent('languageChanged', { detail: { language } });
      window.dispatchEvent(event);
    },
    [merchantSupportedLanguages]
  );

  const getLanguageConfig = useCallback((code: string): LanguageConfig => {
    return LANGUAGE_CONFIGS[code] || { code, label: code.toUpperCase(), flag: '🌐' };
  }, []);

  // All merchant-supported languages — drives content/translation pickers.
  const availableLanguages = merchantSupportedLanguages
    .map((code) => getLanguageConfig(code))
    .filter(Boolean);

  // Subset with admin-panel UI translations — drives the admin language switcher.
  const availableAdminLanguages = availableLanguages.filter((lang) =>
    ADMIN_LANGUAGE_CODES.includes(lang.code)
  );

  const contextValue: LanguageContextType = {
    currentLanguage,
    availableLanguages,
    availableAdminLanguages,
    defaultLanguage: merchantDefaultLanguage,
    changeLanguage,
    getLanguageConfig,
  };

  // Don't render children until language is initialized
  if (!initialized) {
    return null;
  }

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
